/**
 * Tokens em TypeScript: o espelho de `tokens.css` para quem precisa dos
 * valores em JS (o `createTheme` do Mantine, geração de escala por marca).
 *
 * Regra: a fonte dos valores padrão é `tokens.css`. Aqui só o que precisa ser
 * computado — a escala de dez tons a partir da cor principal da marca.
 */

export interface BrandColors {
  primary?: string | null;
  secondary?: string | null;
  /** A cor de destaque (A094); vazia, é a primária. */
  accent?: string | null;
  background?: string | null;
  text?: string | null;
}

export type Shades = [string, string, string, string, string, string, string, string, string, string];

/**
 * Escala padrão da Eva — a escala `wine` do mobile, tom a tom, com a
 * principal no índice 8 (wine[800] = #DC0D79). Manter os índices iguais aos
 * do mobile (wine[N00] → brand-N) é o que permite portar as telas da
 * paciente lendo `--eva-brand-7` onde o mobile lia `wine[700]`.
 */
export const EVA_SHADES: Shades = [
  '#FDF6F9', '#FCE7F3', '#F9D0E7', '#F5B8DA', '#F2A1CE',
  '#EE89C1', '#E851A5', '#E21A89', '#DC0D79', '#B00A61',
];

/** Índice da cor principal na escala (convenção do mobile: wine[800]). */
export const PRIMARY_SHADE = 8;
export const EVA_PRIMARY = EVA_SHADES[PRIMARY_SHADE];
export const EVA_BACKGROUND = '#FBF9F3';
export const EVA_TEXT = '#111827';

/** Largura máxima do "telefone": abaixo disso a tela reproduz o mobile. */
export const PHONE_MAX_PX = 767;
export const PHONE_MEDIA_QUERY = `(max-width: ${PHONE_MAX_PX}px)`;

function hexToRgb(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function rgbToHex([r, g, b]: [number, number, number]): string {
  const c = (v: number) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`.toUpperCase();
}

function mix(a: [number, number, number], b: [number, number, number], t: number): [number, number, number] {
  return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t, a[2] + (b[2] - a[2]) * t];
}

/**
 * Dez tons a partir de uma cor principal: oito clareando até quase branco,
 * a principal no índice 8, uma escurecendo — a mesma forma da escala vinho.
 *
 * O admin repetia a mesma cor dez vezes, o que fazia `variant="light"` e
 * `outline` do Mantine renderizarem blocos sólidos. Aqui a escala é derivada
 * de verdade; para a Eva, o padrão continua sendo a escala desenhada à mão.
 * O mobile sobrescrevia wine[700], wine[800] e wine[900] com a cor primária
 * da marca (e wine[200] com a secundária): aqui isso é brand-7, brand-8,
 * brand-9 e brand-2.
 */
export function shadesFromPrimary(primary: string): Shades {
  if (primary.toUpperCase() === EVA_PRIMARY) return EVA_SHADES;
  const base = hexToRgb(primary);
  if (!base) return EVA_SHADES;
  const white: [number, number, number] = [255, 255, 255];
  const black: [number, number, number] = [0, 0, 0];
  const light = [0.95, 0.9, 0.8, 0.68, 0.56, 0.42, 0.24, 0.1].map((t) => rgbToHex(mix(base, white, t)));
  const dark = [0.2].map((t) => rgbToHex(mix(base, black, t)));
  return [...light, rgbToHex(base), ...dark] as Shades;
}

/**
 * A cor misturada com branco na proporção `t` (0 = a cor, 1 = branco). Cor
 * inválida volta como veio.
 */
export function tint(hex: string, t: number): string {
  const rgb = hexToRgb(hex);
  return rgb ? rgbToHex(mix(rgb, [255, 255, 255], t)) : hex;
}

function corDeDestaque(colors: BrandColors | null | undefined, primaria: string): string {
  return colors?.accent && hexToRgb(colors.accent) ? colors.accent.toUpperCase() : primaria;
}

/** Luminância relativa (WCAG). */
function luminanciaRelativa([r, g, b]: [number, number, number]): number {
  const canal = (v: number) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * canal(r) + 0.7152 * canal(g) + 0.0722 * canal(b);
}

/**
 * A cor escurecida o mínimo para ler como texto sobre branco: 4,5:1, o AA
 * da WCAG para texto normal (A095). Cor que já passa volta como veio — o rosa
 * da Eva Maternidade (#CD3976, ~4,6:1) fica intacto; uma primária clara usada
 * como destaque por falta de escolha (o azul #78C0D7, 2:1) escurece até ler.
 */
export function legivelSobreBranco(hex: string): string {
  const base = hexToRgb(hex);
  if (!base) return hex;
  const preto: [number, number, number] = [0, 0, 0];
  for (let t = 0; t <= 1; t += 0.02) {
    const cor = mix(base, preto, t);
    if (1.05 / (luminanciaRelativa(cor) + 0.05) >= 4.5) return rgbToHex(cor);
  }
  return '#000000';
}

/** Luminância simples (sem a correção gama), a mesma régua de `contrastOn`. */
function luminancia(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 1;
  const [r, g, b] = rgb.map((v) => v / 255);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * A primária clareada só o necessário para chegar à luminância do azul da Eva
 * Maternidade (#78C0D7, 0,69) — o topo das telas de detalhe de conteúdo
 * (12/10/2026). Primária já clara fica como está; a escura ganha branco até
 * um título escuro voltar a ser legível por cima.
 */
export function clarearAte(hex: string, alvo = 0.69): string {
  for (let t = 0; t <= 0.9; t += 0.05) {
    const cor = t === 0 ? hex : tint(hex, t);
    if (luminancia(cor) >= alvo) return cor.toUpperCase();
  }
  return tint(hex, 0.9);
}

/** Branco ou quase-preto, o que contrastar melhor com a cor dada. */
export function contrastOn(hex: string): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return '#FFFFFF';
  const [r, g, b] = rgb.map((v) => v / 255);
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return lum > 0.6 ? EVA_TEXT : '#FFFFFF';
}

export interface BrandTokens {
  shades: Shades;
  primary: string;
  contrast: string;
  secondary: string;
  /** A cor de destaque (A094): a escolhida pela marca, ou a primária. */
  accent: string;
  /**
   * A cor de destaque para TEXTO sobre branco (A095): a mesma, escurecida o
   * mínimo para chegar a 4,5:1. Borda, anel e selo usam `accent`.
   */
  accentText: string;
  /**
   * Tons claros do destaque (A115), para o que a home nova pinta de rosa
   * claro na Eva Maternidade: fundo de banner, selo, círculo de ícone.
   * `accentClaro` mistura 90% de branco; `accentSuave`, 80%.
   */
  accentClaro: string;
  accentSuave: string;
  /**
   * As cores da barra de abas da paciente (A115). A pílula é a primária; a
   * aba ativa, um círculo no destaque com ícone branco, e as outras círculos
   * brancos com ícone na primária. Quando o destaque É a primária — a marca
   * que não escolheu um, a maioria —, o círculo ativo sumia na pílula e a aba
   * ativa virava um buraco entre os brancos. Nesse caso o esquema inverte
   * (13/10/2026, sugestão do Fábio): a ativa é o círculo branco com ícone na
   * primária, e as outras ficam sem círculo, só o ícone branco.
   */
  abas: { ativa: string; iconeAtiva: string; inativa: string; iconeInativa: string };
  /** O tom claro da primária (o mesmo de `tomClaroDaMarca`): fundo das telas da paciente. */
  tomClaro: string;
  /**
   * Os fundos da home (A120), medidos no desenho da Eva Maternidade: o topo
   * (banner, ferramentas, banner de chamada) num tom sólido, a primária com
   * 76% de branco (#DFEEF4 no desenho); o resto num degradê que começa quase
   * branco (92%) e desce até `tomClaro`.
   */
  fundoDoTopo: string;
  fundoClaro: string;
  /** O topo do degradê das telas de detalhe de conteúdo: a primária, clareada se for escura. */
  topoClaro: string;
  background: string;
  text: string;
}

/** As cores das abas: invertidas quando o destaque não se distingue da pílula (ver `BrandTokens.abas`). */
export function coresDasAbas(primaria: string, destaque: string): BrandTokens['abas'] {
  const igual = destaque.toUpperCase() === primaria.toUpperCase();
  return igual
    ? { ativa: '#FFFFFF', iconeAtiva: primaria, inativa: primaria, iconeInativa: '#FFFFFF' }
    : { ativa: destaque, iconeAtiva: '#FFFFFF', inativa: '#FFFFFF', iconeInativa: primaria };
}

export function tokensFromBrand(colors?: BrandColors | null): BrandTokens {
  const primary = colors?.primary || EVA_PRIMARY;
  const shades = shadesFromPrimary(primary);
  // A secundária da marca ocupa o tom 2, como o mobile fazia com wine[200].
  if (colors?.secondary && hexToRgb(colors.secondary)) shades[2] = colors.secondary.toUpperCase();
  return {
    abas: coresDasAbas(shades[PRIMARY_SHADE], corDeDestaque(colors, shades[PRIMARY_SHADE])),
    shades,
    primary: shades[PRIMARY_SHADE],
    contrast: contrastOn(shades[PRIMARY_SHADE]),
    secondary: colors?.secondary || shades[2],
    accent: corDeDestaque(colors, shades[PRIMARY_SHADE]),
    accentText: legivelSobreBranco(corDeDestaque(colors, shades[PRIMARY_SHADE])),
    accentClaro: tint(corDeDestaque(colors, shades[PRIMARY_SHADE]), 0.9),
    accentSuave: tint(corDeDestaque(colors, shades[PRIMARY_SHADE]), 0.8),
    tomClaro: tint(shades[PRIMARY_SHADE], 0.81),
    fundoDoTopo: tint(shades[PRIMARY_SHADE], 0.76),
    fundoClaro: tint(shades[PRIMARY_SHADE], 0.92),
    topoClaro: clarearAte(shades[PRIMARY_SHADE]),
    background: colors?.background || EVA_BACKGROUND,
    text: colors?.text || EVA_TEXT,
  };
}

/** Escreve os tokens da marca no `:root`, sobrescrevendo os padrões do CSS. */
export function applyBrandTokens(tokens: BrandTokens, root: HTMLElement = document.documentElement): void {
  tokens.shades.forEach((tom, i) => root.style.setProperty(`--eva-brand-${i}`, tom));
  root.style.setProperty('--eva-brand', tokens.primary);
  root.style.setProperty('--eva-brand-contrast', tokens.contrast);
  root.style.setProperty('--eva-brand-secondary', tokens.secondary);
  // A cor de destaque (A094) e a sua versão para texto (A095).
  root.style.setProperty('--eva-brand-accent', tokens.accent);
  root.style.setProperty('--eva-brand-accent-text', tokens.accentText);
  root.style.setProperty('--eva-brand-accent-claro', tokens.accentClaro);
  root.style.setProperty('--eva-brand-accent-suave', tokens.accentSuave);
  root.style.setProperty('--eva-aba-ativa', tokens.abas.ativa);
  root.style.setProperty('--eva-aba-icone-ativa', tokens.abas.iconeAtiva);
  root.style.setProperty('--eva-aba-inativa', tokens.abas.inativa);
  root.style.setProperty('--eva-aba-icone-inativa', tokens.abas.iconeInativa);
  root.style.setProperty('--eva-brand-tom-claro', tokens.tomClaro);
  root.style.setProperty('--eva-brand-fundo-topo', tokens.fundoDoTopo);
  root.style.setProperty('--eva-brand-fundo-claro', tokens.fundoClaro);
  root.style.setProperty('--eva-brand-topo-claro', tokens.topoClaro);
  root.style.setProperty('--eva-bg', tokens.background);
  root.style.setProperty('--eva-text', tokens.text);
}
